/**
 * The trial code on the Pro card (subscription requirement R19): what a typed or linked code IS,
 * which face the card's code row wears, and what the checkout is sent.
 *
 * ── Applied is a LOCAL state, and the server is the only judge ──────────────────────────────
 * „Zastosuj" checks nothing with the backend. There is no endpoint that validates a code on its own,
 * and a second rule written here would only disagree with the one the checkout already runs. The
 * code rides with the checkout, and the backend's refusal (`trial_code_invalid`, `…_already_used`,
 * `…_limit_reached`) is what turns it back into a field with a message — `model/webCheckout`.
 *
 * ── Only Pro annual carries a code, and the checkout says so by omission ────────────────────
 * The backend answers a code on any other offer with a 400, which is a client defect rather than a
 * sentence for the trainer. So a code standing on the card while the MONTHLY period is chosen is
 * kept (no silent switch) and sent nowhere: `checkoutCode` is the one field the checkout reads.
 *
 * ── A link survives the sign-in it may have to go through ───────────────────────────────────
 * `/konto?code=PGB` reached while signed out is bounced to `/sign-in` by the shell's guard, and the
 * app has no return-to address. The code is PARKED in the browser for a day instead, and the first
 * signed-in screen hands it back to `/konto`. What is stored is read defensively: a record from an
 * older build, a hand-edited value or a stale one is ignored, never trusted.
 *
 * Pure: the clock arrives as a number, and the store as two strings.
 */

import type { TFunction } from 'i18next';

import {
  TrainWithMeDataEnumsBillingPeriod as BillingPeriod,
  type TrainWithMeDataEnumsBillingPeriod as BillingPeriodValue,
} from '~/shared/api/generated/model';

import type { PlanChangeRoute, PlanOffer } from './webOffers';

/** The field's own ceiling. The wire allows 64; no code the owner configures comes near either. */
export const TRIAL_CODE_MAX = 32;

/**
 * A code as the trainer meant it, or `null` when there is nothing usable in it.
 *
 * Upper case because the backend matches without regard to case (R19) and the card writes the code
 * back in capitals — „Kod PGB" for a link that said `pgb`. Whitespace inside a code is not a code.
 */
export function normalizeTrialCode(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const code = raw.trim().toUpperCase();
  if (code === '' || code.length > TRIAL_CODE_MAX || /\s/u.test(code)) return null;
  return code;
}

/**
 * Which card carries the code row: the recommended one, where it is sold by the year.
 *
 * Not a plan code written down here — the same rule `webOffers` uses to pick the recommended card,
 * so the row follows the top of the range. Today that is Pro, the one level R19 names.
 */
export function carriesTrialCode(offer: PlanOffer): boolean {
  return offer.isRecommended && offer.periods.some((option) => option.period === BillingPeriod.Annual);
}

/**
 * · `refusedByAccount` — the account already holds a subscription (the portal or a store manages
 *   its plan), so no checkout runs here and „Masz kod?" is not offered at all. R11 would refuse it.
 * · `collapsed` — no code on the card. „Masz kod?" and the launch-offer line.
 * · `applied` — a code on the card and the year chosen. The CTA starts the trial.
 * · `annualOnly` — a code on the card and the MONTH chosen. The code stays; the checkout sends none.
 */
export type TrialCodeKind = 'refusedByAccount' | 'collapsed' | 'applied' | 'annualOnly';

export interface TrialCodeFace {
  readonly kind: TrialCodeKind;
  /** What the checkout request carries as `trialCode`. Non-null on `applied` alone. */
  readonly checkoutCode: string | null;
}

export interface TrialCodeFaceInput {
  /** The code applied on the card, already normalised, or `null`. */
  readonly code: string | null;
  /** The period the card is showing. */
  readonly period: BillingPeriodValue | null;
  /** Where a press on the card leads; `null` while the entitlement has not answered. */
  readonly route: PlanChangeRoute | null;
}

export function trialCodeFace({ code, period, route }: TrialCodeFaceInput): TrialCodeFace {
  // Only the checkout route buys anything here. An unanswered entitlement is not yet the checkout
  // route either: the cards are skeletons then, and nothing on them can be pressed.
  if (route !== 'checkout') return { kind: 'refusedByAccount', checkoutCode: null };
  if (code === null) return { kind: 'collapsed', checkoutCode: null };
  if (period !== BillingPeriod.Annual) return { kind: 'annualOnly', checkoutCode: null };
  return { kind: 'applied', checkoutCode: code };
}

export interface TrialCodePressInput extends TrialCodeFaceInput {
  /** What stands in the field, as typed. */
  readonly draft: string;
  /** Whether the field is open. */
  readonly editing: boolean;
}

export interface TrialCodePress {
  /** What the checkout request carries as `trialCode`, or `null` for none. */
  readonly checkoutCode: string | null;
  /** A code typed and never applied that the press puts on the card first, or `null`. */
  readonly applies: string | null;
}

/**
 * What a press on the card's button does with the code: the one applied, or — when the field is
 * open with a code in it that „Zastosuj" never took — that one, applied by the press itself.
 *
 * A typed code the button ignored would buy the year at full price while the trainer believes the
 * trial is in it; the backend still judges it, and a refusal comes back to the field as for any
 * other code. On the month a typed code lands on the card the way an applied one stands there,
 * and is sent nowhere.
 */
export function trialCodeAtPress({ code, draft, editing, period, route }: TrialCodePressInput): TrialCodePress {
  const typed = code === null && editing ? normalizeTrialCode(draft) : null;
  const face = trialCodeFace({ code: code ?? typed, period, route });
  return {
    checkoutCode: face.checkoutCode,
    applies: face.kind === 'refusedByAccount' ? null : typed,
  };
}

/** What a code on the card changes in the card's words. */
export interface TrialCodeCopy {
  /** The fact line under the price, in place of the launch-offer line. */
  readonly fact: string;
  /** The card's button, or `null` where it keeps its ordinary „Wybierz Pro". */
  readonly cta: string | null;
  /** What the screen's live region reads when the code lands — the new button and the new fact. */
  readonly announcement: string | null;
}

/**
 * The copy a code on the card brings, or `null` for a face that brings none (no code, or an
 * account the code is not offered to).
 *
 * The year's total is the offer's own — the catalogue's price, formatted by `webOffers` — so the
 * sentence promises exactly what the payment page will charge after the trial.
 */
export function trialCodeCopy(face: TrialCodeFace, offer: PlanOffer, t: TFunction): TrialCodeCopy | null {
  if (face.kind === 'annualOnly') {
    return { fact: t('web:account.subscription.trial.annualOnly'), cta: null, announcement: null };
  }
  if (face.kind !== 'applied') return null;
  const annual = offer.periods.find((option) => option.period === BillingPeriod.Annual);
  if (annual === undefined) return null;
  const fact = t('web:account.subscription.trial.fact', { total: annual.total });
  const cta = t('web:account.subscription.trial.cta');
  return { fact, cta, announcement: t('web:account.subscription.trial.announce', { cta, fact }) };
}

/* ── Parking a linked code across the sign-in ───────────────────────────────────────────────── */

/** How long a parked code waits for its sign-in. A sign-up's confirmation e-mail fits in it. */
export const PARKED_TRIAL_CODE_TTL_MS = 24 * 60 * 60 * 1000;

/** The record written for a parked code. Serialised by `parkTrialCode`, read by `parkedTrialCode`. */
export function parkTrialCode(code: string, now: number): string {
  return JSON.stringify({ code, parkedAt: now });
}

/**
 * The code a stored record still holds, or `null` — for anything absent, unreadable, misshapen,
 * from the future or older than a day.
 */
export function parkedTrialCode(stored: string | null, now: number): string | null {
  if (stored === null || stored === '') return null;
  let record: unknown;
  try {
    record = JSON.parse(stored);
  } catch {
    return null;
  }
  if (typeof record !== 'object' || record === null) return null;
  const code = 'code' in record ? record.code : undefined;
  const parkedAt = 'parkedAt' in record ? record.parkedAt : undefined;
  if (typeof parkedAt !== 'number' || !Number.isFinite(parkedAt)) return null;
  const age = now - parkedAt;
  if (age < 0 || age > PARKED_TRIAL_CODE_TTL_MS) return null;
  return normalizeTrialCode(code);
}
